import * as React from 'react';
import { I18n } from 'react-redux-i18n';

import { Flex } from '@common/components/Flex';
import { FormControl } from '@common/components/form-control';
import { Button } from '@common/components/Button';
import { Modal, ModalBody, ModalHeader, ModalTitle } from '@common/components/modal';
import { DataImportProps, useController } from './data-import.controller';
import { SpreadsheetImportType } from '@modules/spreadsheets/dto/spreadsheet/import-type';
import { getTestAttributes } from '@common/utils/tests';
import { SPREADSHEET_MODULE_NAME } from '../../constants/shared';
import { ProgressBar } from '@common/components/ProgressBar';

import './data-import.styles.scss';

export const DataImport: React.FC<DataImportProps> = (props) => {
  const { close } = props;

  const {
    handleBlankCellOptionChange,
    selectedBlankCellOption,
    blankCellsOptions,
    IMPORT_MIME_TYPE,
    settings,
    checkboxOptions,
    selectedSetting,
    selectedCheckboxOptions,
    delimiter,
    importing,
    matchedColumnsAmount,
    uploadInputRef,
    importResponseMetadata,
    fallbackProgress,
    handleSettingsChange,
    handleCheckboxOptionsChange,
    handleDelimiterChange,
    handleImport,
    handleMatchedColumnsAmountChange,
    handleImportBtnClick,
    exceptions,
    shouldShowFeedback,
    importBtnText
  } = useController(props);

  // State to track animated progress value for smooth transitions
  const [displayedProcessed, setDisplayedProcessed] = React.useState(0);
  const animationRef = React.useRef<number | null>(null);
  const currentValueRef = React.useRef(0);

  // Animate progress value smoothly when API data changes
  // When completed, show total (100%), otherwise show processed
  React.useEffect(() => {
    if (importResponseMetadata && importResponseMetadata.total > 0) {
      // Use total when completed (to show 100%), otherwise use processed
      const targetValue = importResponseMetadata.completed 
        ? importResponseMetadata.total 
        : importResponseMetadata.processed;
      const currentValue = currentValueRef.current;
      
      // If values are the same, no need to animate
      if (Math.abs(targetValue - currentValue) < 0.5) {
        setDisplayedProcessed(targetValue);
        currentValueRef.current = targetValue;
        return;
      }

      // Cancel any existing animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      // Calculate animation duration based on the difference
      const difference = Math.abs(targetValue - currentValue);
      const duration = Math.min(1000, Math.max(300, difference * 20)); // 300ms to 1000ms
      const startTime = Date.now();
      const startValue = currentValue;

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Use easing function for smooth animation
        const easedProgress = 1 - Math.pow(1 - progress, 3); // Ease-out cubic
        
        const newValue = startValue + (targetValue - startValue) * easedProgress;
        setDisplayedProcessed(newValue);
        currentValueRef.current = newValue;

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          setDisplayedProcessed(targetValue);
          currentValueRef.current = targetValue;
          animationRef.current = null;
        }
      };

      animationRef.current = requestAnimationFrame(animate);
    } else {
      // Reset when no data
      setDisplayedProcessed(0);
      currentValueRef.current = 0;
    }

    // Cleanup function
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [importResponseMetadata?.processed, importResponseMetadata?.total, importResponseMetadata?.completed]);

  return (
    <Modal
      show
      backdrop="static"
      bsClass="bootstrap modal"
      className="SpreadsheetDetailsDataImport"
      animation={false}
      onEscapeKeyDown={close}
      onHide={close}
    >
      <ModalHeader closeButton onHide={close} className="SpreadsheetDetailsDataImport__header">
        <ModalTitle {...getTestAttributes(SPREADSHEET_MODULE_NAME, 'import-data', 'title')}>
          {I18n.t('Import Data')}
        </ModalTitle>
      </ModalHeader>

      <ModalBody className="SpreadsheetDetailsDataImport__content">
        <Flex alignItems="flex-start">
          <div className="SpreadsheetDetailsDataImport__sections">

            {!shouldShowFeedback && (
              <>
                <section className="SpreadsheetDetailsDataImport__section">
                  <span className="SpreadsheetDetailsDataImport__section__title">{I18n.t('Data Import Definition')}</span>

                  {settings.map((x, i) => (
                    <Flex key={x.id} alignItems="center" justify="flex-start">
                      <label className="SpreadsheetDetailsDataImport__section__option">
                        <input 
                          {...getTestAttributes(SPREADSHEET_MODULE_NAME, 'import-data', 'setting', i)}
                          type="radio" 
                          value={x.id}
                          checked={selectedSetting === x.id}
                          onChange={handleSettingsChange} 
                        />
                        {x.label}
                      </label>
                      {x.id === selectedSetting && x.id === SpreadsheetImportType.other && (
                        <FormControl
                          {...getTestAttributes(SPREADSHEET_MODULE_NAME, 'import-data-delimiter', 'input')}
                          className="SpreadsheetDetailsDataImport__section__specific-value-input"
                          value={delimiter}
                          onChange={handleDelimiterChange}
                        />
                      )}
                    </Flex>
                  ))}

                  <Flex className="SpreadsheetDetailsDataImport__section__match-columns" alignItems="center" justify="flex-start">
                    <span>{I18n.t('numberOfMatchedColumns')}</span>
                    <FormControl
                      {...getTestAttributes(SPREADSHEET_MODULE_NAME, 'import-data-matched-columns-amount', 'input')}
                      className="SpreadsheetDetailsDataImport__section__specific-value-input"
                      value={matchedColumnsAmount}
                      onChange={handleMatchedColumnsAmountChange}
                    />
                  </Flex>
                </section>

                <section className="SpreadsheetDetailsDataImport__section">
                  <span className="SpreadsheetDetailsDataImport__section__title">{I18n.t('Data Options')}</span>

                  {checkboxOptions.map((x, i) => (
                    <label key={x.id} className="SpreadsheetDetailsDataImport__section__option">
                      <input 
                        {...getTestAttributes(SPREADSHEET_MODULE_NAME, 'import-data-option', 'checkbox', i)}
                        type="checkbox" 
                        value={x.id}
                        checked={selectedCheckboxOptions.includes(x.id)}
                        onChange={handleCheckboxOptionsChange} 
                      />
                      {x.label}
                    </label>
                  ))}

                  {blankCellsOptions.map((x) => (
                    <label key={x.id} className="SpreadsheetDetailsDataImport__section__option">  
                      <input 
                        {...getTestAttributes(SPREADSHEET_MODULE_NAME, 'import-data-blank-cell', 'radio', x.id)}
                        type="radio" 
                        value={x.id}
                        checked={selectedBlankCellOption === x.id}
                        onChange={handleBlankCellOptionChange} 
                      />
                      {x.label}
                    </label>
                  ))}
                </section>
              </>
            )}

            {shouldShowFeedback && (
              <section className="SpreadsheetDetailsDataImport__section">
                <span className="SpreadsheetDetailsDataImport__section__title">{I18n.t('Import Progress')}</span>

                {/* Progress Bar Section - Show while importing or when we have data from API, never hide */}
                {(importing || (importResponseMetadata && importResponseMetadata.total > 0)) && (
                  <div className="SpreadsheetDetailsDataImport__progress">
                    {importResponseMetadata && importResponseMetadata.total > 0 ? (
                      <ProgressBar
                        // While running, show an indeterminate animated bar for smoother UX.
                        // When completed, snap to 100% using total/total.
                        value={importResponseMetadata.completed ? importResponseMetadata.total : undefined}
                        max={importResponseMetadata.total}
                        color="#4CAF50"
                        height={20}
                        width="100%"
                        label={
                          importResponseMetadata.completed
                            ? `${importResponseMetadata.total}/${importResponseMetadata.total} rows processed (100%)`
                            : importing
                              ? 'Importing...'
                              : ''
                        }
                      />
                    ) : (
                      <ProgressBar
                        value={undefined}
                        max={100}
                        label={importing ? 'Importing...' : ''}
                        color="#4CAF50"
                        height={20}
                        width="100%"
                      />
                    )}
                  </div>
                )}

                {/* Import Results - Combined Summary and Errors */}
                {(importResponseMetadata && importResponseMetadata.completed) || exceptions.length > 0 ? (
                  <div className="SpreadsheetDetailsDataImport__import-results">
                    <h5 className="SpreadsheetDetailsDataImport__section__title SpreadsheetDetailsDataImport__section__title--with-margin">{I18n.t('Import Errors and Warnings')}</h5>
                                        
                    {/* Completion Message */}
                    {importResponseMetadata && importResponseMetadata.completed && (
                      <div className="SpreadsheetDetailsDataImport__completion-message">
                        <span>
                          {importResponseMetadata.processed} out of {importResponseMetadata.total} rows imported
                        </span>
                        {exceptions.length > 0 ? (
                          <span className="SpreadsheetDetailsDataImport__completion-message__error-text">
                            {` with ${exceptions.length} error${exceptions.length !== 1 ? 's' : ''}`}
                          </span>
                        ): (
                          <span className="SpreadsheetDetailsDataImport__completion-message__success-text">
                            {` successfully`}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Error List */}
                    {exceptions.length > 0 && (
                      <div className="SpreadsheetDetailsDataImport__error-list">
                        {exceptions.map((error, index) => (
                          <div key={index} className="SpreadsheetDetailsDataImport__error-item">
                            {error}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}
              </section>
            )}
          </div>
          <div className="SpreadsheetDetailsDataImport__controls">
            {!importing && !importResponseMetadata?.completed && (
              <Button
                bsStyle="primary"
                className="SpreadsheetDetailsDataImport__controls__btn"
                disabled={(SpreadsheetImportType.other === selectedSetting && !delimiter) || importing}
                {...getTestAttributes(SPREADSHEET_MODULE_NAME, 'import-data-submit', 'button')}
                onClick={handleImportBtnClick}
              >
                {I18n.t(importBtnText)}
              </Button>
            )}
            <input 
              hidden
              multiple={false}
              accept={IMPORT_MIME_TYPE[selectedSetting]}
              ref={uploadInputRef as React.LegacyRef<HTMLInputElement>}
              className="SpreadsheetDetailsDataImport__controls__upload-input" 
              type="file" 
              onChange={handleImport} 
            />
            {importResponseMetadata && importResponseMetadata.completed && (
              <Button
                className="SpreadsheetDetailsDataImport__controls__btn"
                {...getTestAttributes(SPREADSHEET_MODULE_NAME, 'import-data-ok', 'button')}
                onClick={close}
              >
                {I18n.t('Close')}
              </Button>
            )}
          </div>
        </Flex>
      </ModalBody>
    </Modal>
  );
};